import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

export type DashboardContactMode = 'unique' | 'active' | 'both';

export type DashboardStats = {
  totalCustomers: number;
  uniqueContactCount: number;
  activeContactCount: number;
  totalSurveySubmissions: number;
  totalQrScans: number;
  surveyCompletionRate: number;
  tngUsed: number;
  tngTotal: number;
  tngPct: number;
  submissionsByMonth: SubmissionsByMonthRow[];
  ethnicBreakdown: EthnicBreakdownRow[];
  ageGroupDistribution: AgeGroupDistributionRow[];
  geographicByState: GeographicByStateRow[];
};

export type EthnicBreakdownRow = {
  race: string;
  count: number;
};

export type GeographicByStateRow = {
  state: string;
  count: number;
};

export type AgeGroupDistributionRow = {
  age: string;
  count: number;
};

export type SubmissionsByMonthRow = {
  month: string;
  monthKey: string;
  uniqueCount: number;
  activeCount: number;
};

export type DashboardQuery = {
  startISO: string;
  endISO: string;
  contactMode: DashboardContactMode;
};

const RETRYABLE_STATUSES = new Set([500, 502, 503, 504]);

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function fetchDashboardResponse(url: string, maxAttempts = 5): Promise<Response> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (RETRYABLE_STATUSES.has(res.status) && attempt < maxAttempts) {
        await wait(400 * attempt);
        continue;
      }

      return res;
    } catch (err) {
      lastError = err;
      if (attempt < maxAttempts) {
        await wait(400 * attempt);
        continue;
      }
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Unable to reach API. Wait for the backend to finish starting, then refresh.');
}

export async function fetchDashboardStats(query: DashboardQuery): Promise<DashboardStats> {
  const apiUrl = getApiOrigin();
  const params = new URLSearchParams({
    startDate: query.startISO,
    endDate: query.endISO,
    contactMode: query.contactMode,
  });

  const res = await fetchDashboardResponse(`${apiUrl}/api/v1/admin/dashboard?${params.toString()}`);
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    data?: DashboardStats;
    error?: string;
  };

  if (!res.ok || !data.ok || !data.data) {
    throw new Error(data.error || `Failed to load dashboard stats (HTTP ${res.status})`);
  }

  return data.data;
}
